import { useTranslation } from '../../../i18n';
import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import {
    Card, Row, Col, Button, Tag, Segmented, Modal, Form, Input, Select, Progress, Alert, Typography,
    Space, Checkbox, Radio, Tooltip, Popconfirm, Empty, List, Avatar, message,
} from 'antd';
import {
    AppstoreOutlined, ExportOutlined, SyncOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined,
} from '@ant-design/icons';
import hostSharedService from '../../../services/hostSharedService';
import { AuthContext } from '../../../contexts/AuthContext';

const CATEGORY_ICONS = { cms: '🌐', blog: '✍️', forum: '💬', shop: '🛒', tool: '🛠️', other: '📦' };
const BUSY = ['installing', 'updating', 'removing'];

const AppIcon = ({ app, size = 40 }) => (
    app?.icon && String(app.icon).startsWith('http')
        ? <Avatar shape="square" size={size} src={app.icon} style={{ background: '#fff' }} />
        : <Avatar shape="square" size={size} style={{ background: '#f0f2f5', fontSize: size * 0.55 }}>{CATEGORY_ICONS[app?.category] || '📦'}</Avatar>
);

const randomDir = () => 'pma-' + Math.random().toString(36).slice(2, 8);

/**
 * App store + installed apps for one shared-hosting site (user console).
 * Install → progress → admin credentials shown once (never stored by the platform).
 */
const AppStore = ({ vhostId, domains = [] }) => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const userEmail = auth?.currentUser?.email || '';

    const [catalog, setCatalog] = useState([]);
    const [installed, setInstalled] = useState([]);
    const [loading, setLoading] = useState(false);
    const [category, setCategory] = useState('all');

    const [app, setApp] = useState(null);          // app being installed
    const [phase, setPhase] = useState('form');    // form | running | done | failed
    const [need, setNeed] = useState(null);        // {php: {...}} | {db: 'needed'|'wrong'}
    const [submitting, setSubmitting] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);    // start response (credentials etc.)
    const [error, setError] = useState('');
    const [form] = Form.useForm();
    const timers = useRef({});

    const domainNames = (domains || []).map((d) => d.domain || d.name).filter(Boolean);

    const load = useCallback(async () => {
        if (!vhostId) return;
        setLoading(true);
        const [c, i] = await Promise.all([hostSharedService.getAppCatalog(vhostId), hostSharedService.getInstalledApps(vhostId)]);
        if (c.success) setCatalog(c.data?.apps || []);
        if (i.success) setInstalled(Array.isArray(i.data) ? i.data : []);
        setLoading(false);
    }, [vhostId]);

    useEffect(() => {
        load();
        const all = timers.current;
        return () => Object.values(all).forEach(clearTimeout);
    }, [load]);

    // background polling for rows that are busy (update / uninstall started from the list)
    const watch = useCallback((id, onEnd) => {
        clearTimeout(timers.current[id]);
        const tick = async () => {
            const r = await hostSharedService.getAppJob(vhostId, id);
            const d = r.success ? r.data : null;
            if (d && d.state === 'running') {
                if (onEnd?.progress) onEnd.progress(d.progress);
                timers.current[id] = setTimeout(tick, 3000);
                return;
            }
            delete timers.current[id];
            onEnd?.end?.(d || { state: 'failed', error: r.error });
            load();
        };
        timers.current[id] = setTimeout(tick, 2000);
    }, [vhostId, load]);

    useEffect(() => {
        installed.filter((r) => BUSY.includes(r.status) && !timers.current[r.id]).forEach((r) => watch(r.id));
    }, [installed, watch]);

    const openInstall = (a) => {
        setApp(a);
        setPhase('form');
        setNeed(null);
        setError('');
        setResult(null);
        setProgress(null);
        form.resetFields();
        form.setFieldsValue({
            domain: domainNames[0],
            subdir: a.slug === 'phpmyadmin' ? randomDir() : (a.subdir_default || ''),
            admin_user: 'siteadmin',
            admin_email: userEmail,
            db_mode: 'reset',
        });
    };

    const submit = async () => {
        let v;
        try {
            v = await form.validateFields();
        } catch {
            return;
        }
        setSubmitting(true);
        setError('');
        const params = {
            slug: app.slug, domain: v.domain, subdir: (v.subdir || '').trim().replace(/^\/+|\/+$/g, ''),
            admin_user: v.admin_user, admin_email: v.admin_email, switch_php: !!v.switch_php,
        };
        if (need?.db) {
            if (v.db_mode === 'reset') params.reset_db_password = true;
            else params.db_password = v.db_password || '';
        }
        const r = await hostSharedService.installApp(vhostId, params);
        setSubmitting(false);
        if (!r.success) {
            const code = r.error_code;
            if (code === 'php_switch_needed') {
                setNeed((n) => ({ ...n, php: r.details?.php }));
            } else if (code === 'db_password_needed' || code === 'db_password_wrong') {
                setNeed((n) => ({ ...n, php: undefined, db: code === 'db_password_wrong' ? 'wrong' : 'needed' }));
                if (code === 'db_password_wrong') setError(r.error);
            } else {
                setError(r.error || t('Installation failed'));
            }
            return;
        }
        setResult(r.data);
        setPhase('running');
        setProgress({ step: 0, steps: 1 });
        watch(r.data.install.id, {
            progress: (p) => setProgress(p),
            end: (d) => {
                if (d.state === 'done') {
                    setPhase('done');
                } else {
                    setPhase('failed');
                    setError(d.error || t('Installation failed'));
                }
            },
        });
        load();
    };

    const closeInstall = () => {
        if (phase === 'done') load();
        setApp(null);
    };

    const doUpdate = async (row) => {
        const r = await hostSharedService.updateApp(vhostId, row.id);
        if (!r.success) {
            message.error(r.error || t('Update failed'));
            return;
        }
        message.info(t('Backing up and updating…'));
        load();
        watch(row.id, { end: (d) => (d.state === 'done' ? message.success(t('Updated to {{v}}', { v: d.install?.version || '' })) : message.error(d.error || t('Update failed'))) });
    };

    const doUninstall = async (row) => {
        const r = await hostSharedService.uninstallApp(vhostId, row.id, row.slug);
        if (!r.success) {
            message.error(r.error || t('Uninstall failed'));
            return;
        }
        load();
        if (r.data?.removed) return;
        watch(row.id, { end: (d) => (d.state === 'removed' ? message.success(t('Uninstalled successfully')) : message.error(d.error || t('Uninstall failed'))) });
    };

    const categories = ['all', ...['cms', 'blog', 'forum', 'shop', 'tool', 'other'].filter((c) => catalog.some((a) => a.category === c))];
    const catLabel = { all: t('All'), cms: t('Website'), blog: t('Blog'), forum: t('Forum'), shop: t('Shop'), tool: t('Tools'), other: t('Other') };
    const shown = category === 'all' ? catalog : catalog.filter((a) => a.category === category);
    const appOf = (slug) => catalog.find((a) => a.slug === slug) || {};

    // ---------------------------------------------------------------- installed
    const statusTag = (row) => {
        if (row.status === 'installing') return <Tag icon={<LoadingOutlined />} color="processing">{t('Installing')}</Tag>;
        if (row.status === 'updating') return <Tag icon={<LoadingOutlined />} color="processing">{t('Updating')}</Tag>;
        if (row.status === 'removing') return <Tag icon={<LoadingOutlined />} color="processing">{t('Uninstalling')}</Tag>;
        if (row.status === 'failed') return <Tooltip title={row.last_error}><Tag color="error">{t('Failed')}</Tag></Tooltip>;
        return null;
    };

    const installedCard = installed.length > 0 && (
        <Card size="small" title={t('Installed apps')} style={{ marginBottom: 16 }}>
            <List
                dataSource={installed}
                renderItem={(row) => {
                    const busy = BUSY.includes(row.status);
                    const actions = [];
                    if (row.status === 'installed') {
                        actions.push(<Button key="open" type="link" size="small" icon={<ExportOutlined />} href={row.admin_url} target="_blank" rel="noopener noreferrer">{t('Open admin')}</Button>);
                    }
                    if (row.update_available && row.status === 'installed') {
                        actions.push(row.update_headless ? (
                            <Popconfirm key="upd" title={t('Back up and update to {{v}}?', { v: row.latest_version })} onConfirm={() => doUpdate(row)}>
                                <Button type="link" size="small" icon={<SyncOutlined />}>{t('Update')}</Button>
                            </Popconfirm>
                        ) : (
                            <Tooltip key="upd" title={t('Update from the app\'s own admin panel')}>
                                <Button type="link" size="small" icon={<SyncOutlined />} disabled>{t('Update')}</Button>
                            </Tooltip>
                        ));
                    }
                    if (!busy) {
                        actions.push(
                            <Popconfirm key="del" title={t('Uninstall {{name}}?', { name: row.name })} description={t('Deletes its files and database tables')}
                                okButtonProps={{ danger: true }} okText={t('Uninstall')} onConfirm={() => doUninstall(row)}>
                                <Button type="link" size="small" danger icon={<DeleteOutlined />}>{t(row.status === 'failed' ? 'Remove' : 'Uninstall')}</Button>
                            </Popconfirm>
                        );
                    }
                    return (
                        <List.Item actions={actions}>
                            <List.Item.Meta
                                avatar={<AppIcon app={{ ...appOf(row.slug), icon: row.icon || appOf(row.slug).icon }} size={36} />}
                                title={<Space size={6}>{row.name}<Tag>{row.version}</Tag>{row.update_available && <Tag color="gold">{row.latest_version}</Tag>}{statusTag(row)}</Space>}
                                description={<a href={row.site_url} target="_blank" rel="noopener noreferrer">{row.site_url.replace(/^https?:\/\//, '')}</a>}
                            />
                        </List.Item>
                    );
                }}
            />
        </Card>
    );

    // ---------------------------------------------------------------- dialog
    const pct = progress && progress.steps ? Math.round(((progress.step || 0) / progress.steps) * 100) : 0;
    const cred = result?.credentials;

    const dialogBody = () => {
        if (phase === 'running') {
            return (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <Progress type="circle" percent={Math.min(pct, 99)} size={96} />
                    <div style={{ marginTop: 12, color: '#888' }}>{t('Installing {{name}}', { name: app.name })}</div>
                </div>
            );
        }
        if (phase === 'done') {
            return (
                <div>
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <CheckCircleOutlined style={{ fontSize: 40, color: '#52c41a' }} />
                    </div>
                    {cred ? (
                        <>
                            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t('Save this password now — it is shown only once.')} />
                            <Typography.Paragraph style={{ marginBottom: 6 }}>
                                {t('Admin')}: <a href={cred.login_url} target="_blank" rel="noopener noreferrer">{cred.login_url}</a>
                            </Typography.Paragraph>
                            <Typography.Paragraph style={{ marginBottom: 6 }}>
                                {t('Username')}: <Typography.Text code copyable>{cred.username}</Typography.Text>
                            </Typography.Paragraph>
                            <Typography.Paragraph style={{ marginBottom: 0 }}>
                                {t('Password')}: <Typography.Text code copyable>{cred.password}</Typography.Text>
                            </Typography.Paragraph>
                        </>
                    ) : (
                        <Typography.Paragraph>
                            <a href={result?.login_url} target="_blank" rel="noopener noreferrer">{result?.login_url}</a>
                            <Tooltip title={t('Log in with the site database user')}><Tag style={{ marginLeft: 8 }}>{t('DB login')}</Tag></Tooltip>
                        </Typography.Paragraph>
                    )}
                    {result?.db_password_reset && (
                        <Alert type="info" showIcon style={{ marginTop: 12 }} message={t('The site database password was reset.')} />
                    )}
                </div>
            );
        }
        if (phase === 'failed') {
            return (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <CloseCircleOutlined style={{ fontSize: 40, color: '#ff4d4f' }} />
                    <div style={{ marginTop: 12 }}>{error}</div>
                </div>
            );
        }
        return (
            <Form form={form} layout="vertical" requiredMark={false}>
                {error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} />}
                <Form.Item name="domain" label={t('Domain')} rules={[{ required: true, message: t('Select a domain') }]}>
                    <Select options={domainNames.map((d) => ({ value: d, label: d }))} />
                </Form.Item>
                <Form.Item name="subdir" label={<Tooltip title={t('Leave empty to install at the domain root')}>{t('Directory')}</Tooltip>}
                    rules={[{ pattern: /^[A-Za-z0-9_-][A-Za-z0-9._-]*(\/[A-Za-z0-9_-][A-Za-z0-9._-]*)*$/, message: t('Invalid directory name') }]}>
                    <Input addonBefore="/" placeholder={t('Root')} allowClear />
                </Form.Item>
                {app?.admin_account && (
                    <Row gutter={12}>
                        <Col span={10}>
                            <Form.Item name="admin_user" label={t('Admin username')}
                                rules={[{ required: true, pattern: /^[a-z][a-z0-9_]{3,14}$/, message: t('4-15 lowercase letters, digits or _') }]}>
                                <Input autoComplete="off" />
                            </Form.Item>
                        </Col>
                        <Col span={14}>
                            <Form.Item name="admin_email" label={t('Admin email')} rules={[{ required: true, type: 'email', message: t('Invalid email address') }]}>
                                <Input autoComplete="off" />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
                {need?.php && (
                    <Form.Item name="switch_php" valuePropName="checked" rules={[{ validator: (_, v) => (v ? Promise.resolve() : Promise.reject(new Error(t('Required')))) }]}>
                        <Checkbox>
                            <Tooltip title={t('Applies to the whole site')}>
                                {t('Switch site PHP to {{v}}', { v: (need.php.target || '').replace(/^php(\d)(\d+)$/, '$1.$2') })}
                            </Tooltip>
                        </Checkbox>
                    </Form.Item>
                )}
                {need?.db && (
                    <>
                        <Form.Item name="db_mode" label={t('Database password')}>
                            <Radio.Group>
                                <Radio value="reset"><Tooltip title={t('Apps already using this database need the new password')}>{t('Reset it')}</Tooltip></Radio>
                                <Radio value="enter">{t('Enter current')}</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(a, b) => a.db_mode !== b.db_mode}>
                            {({ getFieldValue }) => getFieldValue('db_mode') === 'enter' && (
                                <Form.Item name="db_password" rules={[{ required: true, message: t('Required') }]}>
                                    <Input.Password autoComplete="new-password" />
                                </Form.Item>
                            )}
                        </Form.Item>
                    </>
                )}
            </Form>
        );
    };

    const footer = () => {
        if (phase === 'form') {
            return [
                <Button key="c" onClick={closeInstall}>{t('Cancel')}</Button>,
                <Button key="i" type="primary" loading={submitting} onClick={submit}>{t('Install')}</Button>,
            ];
        }
        if (phase === 'running') return null;
        if (phase === 'failed') {
            return [<Button key="b" onClick={() => { setPhase('form'); setError(''); }}>{t('Back')}</Button>, <Button key="c" onClick={closeInstall}>{t('Close')}</Button>];
        }
        return [<Button key="c" type="primary" onClick={closeInstall}>{t('Done')}</Button>];
    };

    // ---------------------------------------------------------------- render
    return (
        <div>
            {installedCard}
            <Card
                size="small"
                title={<Space><AppstoreOutlined />{t('App store')}</Space>}
                extra={categories.length > 2 && <Segmented size="small" value={category} onChange={setCategory} options={categories.map((c) => ({ value: c, label: catLabel[c] }))} />}
                loading={loading && !catalog.length}
            >
                {shown.length === 0 ? <Empty /> : (
                    <Row gutter={[12, 12]}>
                        {shown.map((a) => (
                            <Col xs={24} sm={12} lg={8} key={a.slug}>
                                <Card size="small" hoverable style={{ height: '100%' }} bodyStyle={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                    <AppIcon app={a} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600 }}>{a.name} <span style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>{a.version}</span></div>
                                        <Tooltip title={a.description}>
                                            <div style={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.description}</div>
                                        </Tooltip>
                                        <Tag style={{ marginTop: 4 }} bordered={false}>PHP {a.php_min}{a.php_max ? `–${a.php_max}` : '+'}</Tag>
                                    </div>
                                    <Button type="primary" size="small" onClick={() => openInstall(a)} disabled={!domainNames.length}>{t('Install')}</Button>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Card>

            <Modal
                open={!!app}
                title={app && <Space><AppIcon app={app} size={24} />{app.name}</Space>}
                onCancel={phase === 'running' ? undefined : closeInstall}
                closable={phase !== 'running'}
                maskClosable={false}
                footer={footer()}
                width={520}
                destroyOnClose
            >
                {app && dialogBody()}
            </Modal>
        </div>
    );
};

export default AppStore;
